import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { AuthCard, AuthCardTitle, AuthCardDescription, AuthFormError, AuthFormSuccess, AuthTextButton } from './AuthCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const ForgotPassword: React.FC = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send reset link');
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reset_link_sent'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-8">
      <AuthCard>
        <AuthCardTitle>{t('forgot_password_title')}</AuthCardTitle>
        <AuthCardDescription>{t('forgot_password_description')}</AuthCardDescription>
        {success ? (
          <div>
            <AuthFormSuccess>{t('reset_link_sent')}</AuthFormSuccess>
            <Button type="button" variant="inverse" className="w-full" onClick={() => navigate('/')}>
              {t('back')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <Label htmlFor="email" className="mb-2 block">
                {t('email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            {error && <AuthFormError>{error}</AuthFormError>}
            <Button type="submit" variant="inverse" className="mb-4 w-full" disabled={isLoading}>
              {isLoading ? t('sending_reset_link') : t('send_reset_link')}
            </Button>
            <div className="text-center text-sm text-muted-foreground">
              <AuthTextButton onClick={() => navigate('/')}>
                {t('back')}
              </AuthTextButton>
            </div>
          </form>
        )}
      </AuthCard>
    </div>
  );
};
